import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    useTheme,
    InputAdornment,
    Avatar,
    Skeleton,
    Chip,
    Paper,
    Stack
} from '@mui/material';
import {
    LocationCity,
    PeopleAlt,
    Search,
    HolidayVillage,
    HowToVote,
    Person,
    Lock
} from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { Constituency } from '../../types/constituency';

const formatCount = (value?: number) => (value ?? 0).toLocaleString('en-IN');

const Citizens = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const theme = useTheme();

    // State
    const [search, setSearch] = useState('');

    // Fetch Constituencies
    const { data: constituenciesData, isLoading } = useQuery({
        queryKey: ['constituencies', search],
        queryFn: async () => {
            const params: any = { per_page: 100 };
            if (search) params.search = search;
            const res = await api.get('/tenant/constituencies', { params });
            return res.data;
        },
        placeholderData: keepPreviousData,
    });

    const constituencies = constituenciesData?.data?.data || constituenciesData?.data || [];
    const totalConstituencies = constituencies.length;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PeopleAlt fontSize="large" sx={{ opacity: 0.9 }} />
                        Constituency Management
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.8, maxWidth: 600 }}>
                        View your constituencies and their respective voting booths and citizens.
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Chip
                            label={`${totalConstituencies} Constituencies`}
                            color="primary"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                        <Chip
                            icon={<Lock sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
                            label="Read-only"
                            variant="outlined"
                            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                        />
                    </Stack>
                </Box>
            </Paper>

            {/* Controls Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    All Constituencies
                </Typography>
                <TextField
                    placeholder="Search by name..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 }, bgcolor: 'background.paper', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {isLoading ? (
                    // Loading Skeletons
                    Array.from(new Array(4)).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: 280, borderRadius: 3 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={48} height={48} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="80%" height={32} />
                                            <Skeleton variant="text" width="50%" />
                                        </Box>
                                    </Box>
                                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (constituencies as Constituency[]).length > 0 ? (
                    (constituencies as Constituency[]).map((item) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer', // Make clickable
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                                onClick={() => navigate(`/admin/citizens/${item.id}`)}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: theme.palette.secondary.main,
                                                width: 50,
                                                height: 50,
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                            }}
                                        >
                                            {item.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <IconButton size="small" sx={{ opacity: 0.6 }}>
                                                <LocationCity fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.3, mb: 0.5 }}>
                                        {item.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <HolidayVillage fontSize="small" color="primary" />
                                                <Typography variant="caption" fontWeight="medium">Villages: {formatCount(item.villages_count)}</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <HowToVote fontSize="small" color="secondary" />
                                                <Typography variant="caption" fontWeight="medium">Voting Booths: {formatCount(item.voting_booths_count)}</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                                                <Person fontSize="small" color="success" />
                                                <Typography variant="caption" fontWeight="medium">Citizens: {formatCount(item.citizens_count)}</Typography>
                                            </Box>
                                        </Paper>
                                    </Box>

                                </CardContent>
                                {/* Removed CardActions with Edit/Delete buttons */}
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '2px dashed',
                                borderColor: 'divider',
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ mb: 2, opacity: 0.5 }}>
                                <PeopleAlt sx={{ fontSize: 60, color: 'text.secondary' }} />
                            </Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {search ? `No constituencies found matching "${search}"` : "No constituencies found"}
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default Citizens;
